import { useEffect, useState } from 'react';

import { Listbox } from '@headlessui/react';
import { matchSorter } from 'match-sorter';

import { useSearchBoxContext } from '@/utils/hooks/context/searchBox/useSearchBoxContext';

// Utils
import { sortGroups } from '@/utils/sort/profiles/sortGroups';
import { sortSocialProfiles } from '@/utils/sort/profiles/sortSocialProfiles';

// Components
import Icon from '@/components/base/Icon';
import { SelectComponent } from '@/components/base/Select/SelectComponent/SelectComponent';
import type { GroupsFormattedType } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab';
// Types
import type { SocialProfileFormattedType } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab';

const options = [
    { value: 'sort_asc', label: 'A - Z' },
    { value: 'sort_desc', label: 'Z - A' }
] as const;

function SelectButton({ selected }: { selected: (typeof options)[number] }) {
    return (
        <Listbox.Button data-testid="select-button" className="flex items-center gap-1">
            <span>
                <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">Sort by:</span>{' '}
                <span>{selected.label}</span>
            </span>
            <Icon icon="chevron-bottom" className="text-icoGray-400 dark:text-icoDarkMode-wolf" />
        </Listbox.Button>
    );
}

type SelectSortItemProps<TType> = {
    type: TType;
};

export function SelectSortItem<
    TType extends 'profiles' | 'groups',
    TContext extends SocialProfileFormattedType | GroupsFormattedType
>({ type }: SelectSortItemProps<TType>) {
    const [selectedSort, setSelectedSort] = useState<(typeof options)[number]>(options[0]);

    const { dataContext, dispatch } = useSearchBoxContext<TContext>();
    const { filteredData } = dataContext;

    useEffect(() => {
        const sortedData = matchSorter(filteredData, '', {
            baseSort: (a, b) => {
                if (type === 'groups') {
                    return sortGroups(
                        selectedSort.value,
                        a.item as GroupsFormattedType,
                        b.item as GroupsFormattedType
                    );
                }

                return sortSocialProfiles(
                    selectedSort.value,
                    a.item as SocialProfileFormattedType,
                    b.item as SocialProfileFormattedType
                );
            }
        });
        dispatch({ type: 'SET_FILTERED_DATA', payload: sortedData });
    }, [selectedSort, JSON.stringify(filteredData)]);

    return (
        <SelectComponent
            button={({ selected }) => SelectButton({ selected })}
            options={options}
            selected={selectedSort}
            setSelected={setSelectedSort}
        />
    );
}
